import { useState, useEffect } from 'react';
import { apiFetch } from './api';

const DEFAULT_COLOR = '#3b82f6';

export default function AssetStatusSettings() {
  const [statuses, setStatuses] = useState([]);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(DEFAULT_COLOR);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState(null);

  async function loadStatuses() {
    try {
      setLoading(true);
      setError('');
      const data = await apiFetch('/asset-status');
      setStatuses(
        (Array.isArray(data) ? data : []).map((status) => ({
          ...status,
          draftName: status.name || '',
          draftColor: status.color || DEFAULT_COLOR,
        }))
      );
    } catch (e) {
      setError(e.message || 'Failed to load statuses');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadStatuses();
  }, []);

  async function addStatus() {
    if (!newName.trim()) {
      alert('Please enter a status name');
      return;
    }

    try {
      await apiFetch('/asset-status', {
        method: 'POST',
        body: JSON.stringify({ name: newName.trim(), color: newColor })
      });
      setNewName('');
      setNewColor(DEFAULT_COLOR);
      await loadStatuses();
    } catch (e) {
      alert(e.message || 'Failed to add status');
    }
  }

  function handleStatusChange(id, field, value) {
    setStatuses((prev) =>
      prev.map((status) =>
        status.id === id ? { ...status, [field]: value } : status
      )
    );
  }

  const statusHasChanges = (status) => {
    const trimmedDraft = (status.draftName || '').trim();
    const originalName = status.name || '';
    const originalColor = status.color || DEFAULT_COLOR;
    const draftColor = status.draftColor || DEFAULT_COLOR;
    return trimmedDraft !== originalName || draftColor !== originalColor;
  };

  async function saveStatus(status) {
    const trimmedName = (status.draftName || '').trim();
    if (!trimmedName) {
      alert('Please enter a status name');
      return;
    }
    try {
      setSavingId(status.id);
      await apiFetch(`/asset-status/${status.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: trimmedName, color: status.draftColor || DEFAULT_COLOR })
      });
      await loadStatuses();
    } catch (e) {
      alert(e.message || 'Failed to update status');
    } finally {
      setSavingId(null);
    }
  }

  async function deleteStatus(id, name) {
    const confirmed = window.confirm(`Delete status "${name}"?`);
    if (!confirmed) return;

    try {
      await apiFetch(`/asset-status/${id}`, {
        method: 'DELETE'
      });
      await loadStatuses();
    } catch (e) {
      alert(e.message || 'Failed to delete status');
    }
  }

  return (
    <div className="card boat-config-card-compact">
      <div className="boat-config-header-compact">
        <div>
          <h2 className="h2" style={{ marginTop: 0, marginBottom: 4 }}>Asset Status Configuration</h2>
          <p className="help" style={{ margin: 0 }}>
            Manage status values for equipment assets
          </p>
        </div>
        {loading && <span className="help">Loading…</span>}
        {error && <span className="help boat-config-error">{error}</span>}
      </div>

      <div className="boat-config-form-compact">
        <input
          className="input"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="e.g., Available, In Repair, Retired"
          onKeyDown={(e) => e.key === 'Enter' && addStatus()}
          style={{ flex: 1, minWidth: 150 }}
        />

        <input
          type="color"
          value={newColor}
          onChange={(e) => setNewColor(e.target.value)}
          title="Status color"
          style={{ width: 50, height: 36, cursor: 'pointer', border: '1px solid #ccc', borderRadius: 4 }}
        />

        <button className="btn" onClick={addStatus} style={{ whiteSpace: 'nowrap' }}>
          + Add
        </button>
      </div>

      <div className="boat-config-tab-content">
        {statuses.length === 0 ? (
          <p className="help" style={{ textAlign: 'center', padding: 16 }}>
            No statuses configured yet. Add some above!
          </p>
        ) : (
          <ul className="list" style={{ margin: 0 }}>
            {statuses.map((status) => {
              const colorValue = status.draftColor || DEFAULT_COLOR;
              const dirty = statusHasChanges(status);
              const isSaving = savingId === status.id;
              return (
                <li
                  key={status.id}
                  className="boat-config-item-compact"
                  style={{ flexWrap: 'wrap', gap: 12, alignItems: 'center' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '1 1 260px' }}>
                    <input
                      className="input"
                      value={status.draftName}
                      onChange={(e) => handleStatusChange(status.id, 'draftName', e.target.value)}
                      placeholder="Status name"
                    />
                    <input
                      type="color"
                      value={colorValue}
                      onChange={(e) => handleStatusChange(status.id, 'draftColor', e.target.value)}
                      title="Status color"
                      style={{ width: 40, height: 36, cursor: 'pointer', border: '1px solid #ccc', borderRadius: 4 }}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button
                      type="button"
                      className="btn small"
                      onClick={() => saveStatus(status)}
                      disabled={!dirty || isSaving}
                    >
                      {isSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      className="btn-icon danger"
                      onClick={() => deleteStatus(status.id, status.name)}
                      title="Delete status"
                    >
                      ×
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="boat-config-hint-compact">
        <div className="boat-config-hint-content">
          <p className="help" style={{ margin: 0 }}>
            <strong>Examples:</strong> Available, In Repair, Damaged, Retired, Reserved
          </p>
        </div>
      </div>
    </div>
  );
}
